import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { toPlainMessage, type PlainMessage } from "@bufbuild/protobuf";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Editor } from "gitpod-next-api/gitpod/v1/editor_pb";
import { type Environment } from "gitpod-next-api/gitpod/v1/environment_pb";
import { autoPaginate } from "@/queries/pagination";

export type PlainEditor = PlainMessage<Editor>;

export const toPlainEditor = (editor: Editor): PlainEditor => {
    return toPlainMessage(editor);
};

export const editorQueryKey = {
    list: () => keyWithPrincipal(["editors", "list", "all"]),
    url: (editorId: string, environmentId: string, organizationId: string) =>
        keyWithPrincipal(["editors", "url", editorId, environmentId, organizationId]),
};

/**
 * @deprecated Use useEditorURLQuery instead
 */
export const useEditorURL = () => {
    const api = useGitpodAPI();
    return useMutation({
        mutationFn: ({
            editorId,
            environment,
        }: {
            editorId: string;
            environment?: Environment | PlainMessage<Environment>;
        }) => {
            const environmentId = environment?.id;
            const organizationId = environment?.metadata?.organizationId;
            if (!environmentId || !organizationId) {
                throw new Error("No environment or organization ID provided");
            }
            return api.editorService.resolveEditorURL({
                environmentId,
                organizationId,
                editorId,
            });
        },
    });
};

export const useEditorURLQuery = ({
    editorId,
    environment,
}: {
    editorId?: string;
    environment?: Environment | PlainMessage<Environment>;
}) => {
    const api = useGitpodAPI();
    const environmentId = environment?.id;
    const organizationId = environment?.metadata?.organizationId;

    return useQuery({
        queryKey: editorQueryKey.url(editorId || "", environmentId || "", organizationId || ""),
        queryFn: async () => {
            if (!editorId || !environmentId || !organizationId) {
                return undefined;
            }
            return api.editorService.resolveEditorURL({
                environmentId,
                organizationId,
                editorId,
            });
        },
        // Disable caching
        gcTime: 0,
        staleTime: 0,
        enabled: !!environmentId && !!organizationId,
    });
};

export type PlainEditorList = {
    editors: PlainEditor[];
};

export const useListEditors = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: editorQueryKey.list(),
        queryFn: async (): Promise<PlainEditorList> => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const editors = await autoPaginate(
                (params) => api.editorService.listEditors(params),
                {},
                (response) => response.editors,
            );

            return {
                editors: editors.map(toPlainEditor),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 1_000 * 60 * 5, // 5 minutes
        gcTime: 1_000 * 60 * 60 * 24 * 7, // 1 week
        refetchOnReconnect: "always",
    });
};
